import { onDestroy } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { createList, type ListInstance, type ListResult } from "@lib/list";
import { createLoader } from "@lib/loader";

/**
 * Integrations directory — search + faceted filtering over the Providers CMS.
 *
 * Replaces the retired Jetboost setup. Two problems Jetboost was solving that
 * this has to solve too:
 *
 *  1. **Webflow caps a Collection List at 100 items** and there are 239
 *     providers, so filtering only what the server rendered would silently
 *     miss 58% of the catalogue. `@lib/loader` walks Webflow's own
 *     `?<id>_page=N` links to pull the rest (~490KB encoded for pages 2–3,
 *     fetched lazily — never on first paint).
 *
 *  2. **A card can't express a multi-reference field as one attribute.** Each
 *     referenced value is rendered as its own `[data-list-collect]` element
 *     (a nested Collection List) and `@lib/list` folds them back into a
 *     single comma-joined field.
 *
 * Filter semantics: AND across groups, OR within a group.
 *
 * ## Markup contract
 *
 * Mount on the element wrapping BOTH the sidebar and the grid:
 * `data-module="int-filter"`.
 *
 * | Attribute                  | On                                    |
 * |----------------------------|---------------------------------------|
 * | `int-filter="grid"`        | the `.w-dyn-items` holding the cards   |
 * | `int-filter="item"`        | each card (data lives here)            |
 * | `int-filter="search"`      | the sidebar search `<input>`           |
 * | `int-filter="reset"`       | the "Clear all" button                 |
 * | `int-filter="group"`       | each filter dropdown, + `data-field`   |
 * | `int-filter="option"`      | each option row, + `data-value`        |
 * | `int-filter="chips"`       | applied-filter chip container          |
 * | `int-filter="chip"`        | one chip, used as a template           |
 * | `int-filter="count"`       | optional — results count text          |
 * | `int-filter="empty"`       | optional — empty state                 |
 *
 * `data-field` values are arbitrary but must match the field name the cards
 * collect into (`cat`, `type`, `strength`, `plan`, `dpcat`).
 *
 * Options: `data-value` wins; otherwise the option's own hidden CMS input,
 * otherwise its visible label text. Cards and options must draw the value
 * from the SAME source (both slugs, or both names) — a Webflow slug can drift
 * from its name after a rename (`Data Warehouses` still slugs `databases`).
 */

// reveal batch — how many cards are added per infinite-scroll step
const BATCH = 24;
// how far below the grid the sentinel triggers the next batch
const ROOT_MARGIN_PX = 600;
const ROOT_MARGIN = `${ROOT_MARGIN_PX}px`;
const SEARCH_DEBOUNCE = 150;

// entrance animation for newly revealed cards
const IN_DURATION = 0.45;
const IN_STAGGER = 0.02;
const IN_STAGGER_MAX = 0.3; // cap total stagger so a big batch never crawls
const EASE = "power2.out";

const q = (el: ParentNode, v: string) =>
  el.querySelector<HTMLElement>(`[int-filter="${v}"]`);

const qAll = (el: ParentNode, v: string) =>
  Array.from(el.querySelectorAll<HTMLElement>(`[int-filter="${v}"]`));

/**
 * Lowercase → dashes, so a CMS slug and a human label normalise to the same
 * key. Identity on values that are already slugs, which lets a group mix
 * slug-valued CMS options with plain-text ones (Plan compatibility).
 */
const norm = (v: string) =>
  v
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** The element that actually occupies a grid cell — hiding the inner card
 *  would leave an empty track behind. */
const cell = (el: HTMLElement): HTMLElement =>
  el.closest<HTMLElement>(".w-dyn-item") ?? el;

/**
 * Stamp each cell with its position in the collection: page first, then
 * position within the page.
 *
 * Pages don't arrive in CMS order once `backfill` is on — a visitor who lands
 * on `?…_page=2` gets page 2 from the server and pages 1, 3, 4 from the
 * loader, in that order. Without a stamp the grid would read 101…200, 1…100,
 * 201…, and the reveal batches would follow the same jumble.
 */
const stampOrder = (cells: HTMLElement[], page: number) => {
  cells.forEach((el, i) => {
    el.dataset.pageOrder = String(page * 1000 + i);
  });
};

export default function (element: HTMLElement, dataset: DOMStringMap) {
  // ---- resolve markup ------------------------------------------------

  const grid = q(element, "grid") ?? element.querySelector<HTMLElement>(".w-dyn-items");
  if (!grid) return;

  const itemSelector = '[int-filter="item"]';
  const cardEls = qAll(grid, "item");
  if (!cardEls.length) return;

  // The search field and "Clear all" button live inside shared Webflow
  // components ("Sidenav / Search", "Sidenav / Label"), so an attribute can't
  // be set on them per-page without editing the component definition. Both
  // components already expose a class prop — the hook Jetboost used — so a
  // class is the page-scoped way in. Attribute wins where it exists.
  const searchInput = (q(element, "search") ??
    element.querySelector<HTMLElement>(
      ".int-search",
    )) as HTMLInputElement | null;
  const resetBtn =
    q(element, "reset") ?? element.querySelector<HTMLElement>(".int-reset");
  const chipsWrap = q(element, "chips");
  const countEl = q(element, "count");
  const emptyEl = q(element, "empty");
  const groups = qAll(element, "group");

  // Chip template — lifted out of the DOM on mount so the static Webflow
  // placeholders never flash before the real chips render. The chips are
  // component instances here, which can't carry a per-page attribute, so the
  // container's first child stands in as the template.
  const chipTemplate = chipsWrap
    ? (q(chipsWrap, "chip") ?? (chipsWrap.firstElementChild as HTMLElement | null))
    : null;
  const chipProto = chipTemplate?.cloneNode(true) as HTMLElement | undefined;
  if (chipsWrap) chipsWrap.replaceChildren();

  const syncUrl = dataset.urlSync !== "off";

  /** How the grid is addressed inside a *fetched* page. The attribute is the
   *  reliable handle; `data-grid-selector` overrides it for the case where
   *  the attributes exist only on this document (local testing against a
   *  published page that doesn't carry them yet). */
  const gridSelector =
    dataset.gridSelector ||
    (grid.hasAttribute("int-filter")
      ? '[int-filter="grid"]'
      : "." + Array.from(grid.classList).join("."));

  // ---- build the item set --------------------------------------------
  // The list operates on grid cells, but the data (and any nested collect
  // lists) live on the card inside. Hoist the card's data-* onto the cell so
  // both stay in one place downstream.

  function ingest(cards: HTMLElement[]): HTMLElement[] {
    return cards.map((card) => {
      const el = cell(card);
      if (el !== card) {
        for (const key in card.dataset) {
          if (el.dataset[key] === undefined) el.dataset[key] = card.dataset[key];
        }
      }
      // Search targets name + description explicitly rather than the card's
      // full text, which also contains tooltip copy ("Billable Action",
      // "Clay credits") that would produce baffling matches.
      if (el.dataset.name === undefined) {
        el.dataset.name = card.querySelector("h3")?.textContent?.trim() ?? "";
      }
      if (el.dataset.desc === undefined) {
        el.dataset.desc = card.querySelector("p")?.textContent?.trim() ?? "";
      }
      return el;
    });
  }

  // ---- CMS pagination -------------------------------------------------
  // Built before the list so the server-rendered cells can be stamped with
  // the page the visitor actually landed on.

  const loader = createLoader({
    container: grid,
    // Scoped to the grid, so items are the grid's own cells — not the cards
    // inside them, which would lose their layout wrapper on adoption.
    scopeSelector: gridSelector,
    itemSelector: ":scope > *",
    // Webflow's pagination only links forward, so arriving on `?…_page=2`
    // leaves page 1 unreachable — the first 100 providers would silently drop
    // out of every filter and every search. Fetch the earlier pages too.
    backfill: true,
    onLoad: (cells, { page }) => {
      // A fetched cell only counts if it actually carries a card.
      const withCards = cells.filter((c) => c.querySelector(itemSelector));
      const ingested = ingest(
        withCards.map((c) => c.querySelector<HTMLElement>(itemSelector)!),
      );
      stampOrder(ingested, page);
      for (const el of ingested) {
        el.style.display = "none"; // render() decides when it paints
        grid!.appendChild(el);
      }
      // Forward pages append in order already; only a backfilled page lands
      // out of sequence and needs the grid put back together.
      if (page < loader.currentPage) reorderGrid();
      list.addItems(ingested);
    },
    onError: (err) => console.warn("[int-filter]", err.message),
  });

  /** Re-lay the grid in collection order after a page arrives out of turn. */
  function reorderGrid() {
    const cells = Array.from(grid!.children) as HTMLElement[];
    cells.sort(
      (a, b) => Number(a.dataset.pageOrder ?? 0) - Number(b.dataset.pageOrder ?? 0),
    );
    grid!.append(...cells);
  }

  const initialCells = ingest(cardEls);
  stampOrder(initialCells, loader.currentPage);

  const list: ListInstance = createList(initialCells, {
    matchNormalize: norm,
    statusAttr: "data-int-status",
    // Typo tolerance on the provider name only. Descriptions are long enough
    // that a subsequence match would return most of the catalogue.
    fuzzyFields: ["name"],
    onChange: (result) => {
      if (ready) handleChange(result);
    },
  });

  let allPagesLoaded = !loader.hasMore;

  // ---- render ---------------------------------------------------------

  // Unfiltered, everything Webflow rendered stays on screen — trimming 100
  // cards to a batch on boot would collapse the page height for no gain.
  // Batching only kicks in once a filter or query narrows the set, where it
  // keeps the transition cheap.
  let revealCount = Infinity;
  let visible = new Set<HTMLElement>(initialCells);
  let ready = false;

  // Keep the item order — and so the reveal batches — in collection order no
  // matter which page each item arrived on. A no-op when the visitor came in
  // on page 1, which is the usual case.
  //
  // Has to come after `ready` exists: sort() runs a compute pass, which calls
  // onChange, which reads it.
  list.sort("pageOrder", "asc", "number");

  function render(active: HTMLElement[], animate = true) {
    const show =
      revealCount === Infinity ? active : active.slice(0, revealCount);
    const showSet = new Set(show);

    const entering: HTMLElement[] = [];

    for (const el of visible) {
      if (!showSet.has(el)) {
        gsap.killTweensOf(el);
        gsap.set(el, { clearProps: "opacity,transform" });
        el.style.display = "none";
      }
    }

    for (const el of show) {
      if (!visible.has(el)) {
        el.style.display = "";
        entering.push(el);
      }
    }

    visible = showSet;

    if (entering.length && animate && !reduced) {
      gsap.fromTo(
        entering,
        { opacity: 0, y: 8 },
        {
          opacity: 1,
          y: 0,
          duration: IN_DURATION,
          ease: EASE,
          stagger: Math.min(IN_STAGGER, IN_STAGGER_MAX / entering.length),
          overwrite: true,
        },
      );
    }
  }

  function updateUI(result: ListResult) {
    if (countEl) countEl.textContent = String(result.passing);

    if (emptyEl) {
      // Only claim "no results" once the whole catalogue is in memory —
      // otherwise a narrow filter reads as empty while pages 2–3 are still
      // in flight.
      const empty = result.passing === 0 && allPagesLoaded;
      emptyEl.style.display = empty ? "" : "none";
    }

    renderChips();
    syncOptionStates();
    if (syncUrl) writeUrl();
  }

  // ---- applied-filter chips -------------------------------------------

  function renderChips() {
    if (!chipsWrap || !chipProto) return;

    const frag = document.createDocumentFragment();

    for (const [field, values] of list.activeFilters) {
      for (const value of values) {
        const chip = chipProto.cloneNode(true) as HTMLElement;
        const label = labelFor(field, value);

        // The template's inner text node is whatever placeholder Webflow
        // shipped ("Tag") — replace text without disturbing the close icon.
        const textEl =
          chip.querySelector<HTMLElement>("[int-filter='chip-text']") ??
          chip.querySelector<HTMLElement>("div");
        if (textEl) textEl.textContent = label;
        else chip.textContent = label;

        chip.removeAttribute("href");
        chip.style.display = "";
        chip.dataset.field = field;
        chip.dataset.value = value;
        frag.appendChild(chip);
      }
    }

    chipsWrap.replaceChildren(frag);
    chipsWrap.style.display = list.filterCount ? "" : "none";
  }

  /** Human label for a chip — falls back to the raw value if the option
   *  that produced it has since left the DOM. */
  function labelFor(field: string, value: string): string {
    for (const group of groups) {
      if (group.dataset.field !== field) continue;
      for (const opt of qAll(group, "option")) {
        if (norm(optionValue(opt)) === norm(value)) return optionLabel(opt);
      }
    }
    return value;
  }

  const onChipClick = (e: Event) => {
    const chip = (e.target as HTMLElement).closest<HTMLElement>("[data-field]");
    if (!chip || !chipsWrap?.contains(chip)) return;
    e.preventDefault();
    const { field, value } = chip.dataset;
    if (field && value) list.toggleFilter(field, value);
  };

  // ---- options ---------------------------------------------------------

  function optionValue(opt: HTMLElement): string {
    // Explicit wins — the static Plan compatibility options carry `data-value`.
    if (opt.dataset.value) return opt.dataset.value;

    // Otherwise the visible label, which is the taxonomy *Name* — the same
    // field the cards' collectors are bound to.
    //
    // Deliberately NOT the slug from the leftover Jetboost embed: a Webflow
    // slug drifts from its name after a rename (`Data Warehouses` still slugs
    // `databases`), so a name on one side and a slug on the other silently
    // fails to match. Both sides read names; the embeds are now unused.
    return optionLabel(opt);
  }

  function optionLabel(opt: HTMLElement): string {
    return (
      opt.querySelector(".w-form-label")?.textContent?.trim() ??
      opt.textContent?.trim() ??
      ""
    );
  }

  function checkboxOf(opt: HTMLElement): HTMLInputElement | null {
    return opt.matches('input[type="checkbox"]')
      ? (opt as HTMLInputElement)
      : opt.querySelector<HTMLInputElement>('input[type="checkbox"]');
  }

  /** Push list state back onto the checkboxes — needed after URL restore,
   *  chip removal and Clear all, none of which touch the inputs. */
  function syncOptionStates() {
    for (const group of groups) {
      const field = group.dataset.field;
      if (!field) continue;
      for (const opt of qAll(group, "option")) {
        const box = checkboxOf(opt);
        if (!box) continue;
        const on = list.hasFilter(field, optionValue(opt));
        if (box.checked !== on) box.checked = on;
        // Webflow's styled checkbox mirrors state with a class when the
        // input is visually replaced.
        opt
          .querySelector(".w-checkbox-input")
          ?.classList.toggle("w--redirected-checked", on);
      }
    }
  }

  // ---- change handling --------------------------------------------------

  let loadingAll = false;

  function handleChange(result: ListResult) {
    // Render what's already loaded straight away rather than waiting on the
    // remaining CMS pages — results only ever grow as they arrive, so the
    // grid fills in rather than reshuffling. `data-int-loading` is on the
    // root throughout, and the empty state stays suppressed until every page
    // is in, so a slow network never reads as "no results".
    revealCount = isNarrowed() ? BATCH : Infinity;
    render(result.active);
    updateUI(result);

    if (loader.hasMore && isNarrowed()) void loadRemaining();

    // A filter can shrink the grid enough that the sentinel is already on
    // screen. No transition means no IO callback, so prime the pump here.
    if (!loadingAll) schedulePump();
  }

  const isNarrowed = () => list.filterCount > 0 || !!list.searchQuery;

  async function loadRemaining() {
    if (loadingAll || !loader.hasMore) return;

    loadingAll = true;
    element.setAttribute("data-int-loading", "");
    // Each page calls back into list.addItems → onChange → handleChange, so
    // the grid grows page by page; this only settles the final state.
    await loader.loadAll();
    loadingAll = false;
    allPagesLoaded = !loader.hasMore;
    element.removeAttribute("data-int-loading");

    updateUI(list.getResult());
    // handleChange's pump is suppressed for the whole of the load (loadingAll
    // is set before it runs), so this is the only chance to top the grid up
    // to the fold once the full catalogue is in.
    schedulePump();
  }

  // ---- infinite scroll ---------------------------------------------------

  const sentinel = document.createElement("div");
  sentinel.setAttribute("aria-hidden", "true");
  sentinel.style.cssText = "width:100%;height:1px;pointer-events:none";
  grid.after(sentinel);

  let advancing = false;

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      void pump();
    },
    { rootMargin: ROOT_MARGIN },
  );

  function sentinelInView(): boolean {
    const r = sentinel.getBoundingClientRect();
    return (
      r.top <= window.innerHeight + ROOT_MARGIN_PX && r.bottom >= -ROOT_MARGIN_PX
    );
  }

  /** One reveal-or-load step. Returns whether anything actually changed. */
  async function step(): Promise<boolean> {
    const result = list.getResult();

    // More already-loaded matches waiting → just reveal the next batch.
    if (revealCount !== Infinity && revealCount < result.passing) {
      revealCount = Math.min(revealCount + BATCH, result.passing);
      render(result.active);
      updateUI(result);
      return true;
    }

    // Otherwise pull another CMS page, if there is one.
    if (loader.hasMore && !loader.isLoading) {
      const before = list.items.length;
      await loader.load();
      allPagesLoaded = !loader.hasMore;
      const next = list.getResult();
      if (revealCount !== Infinity) {
        revealCount = Math.min(revealCount + BATCH, next.passing);
      }
      render(next.active);
      updateUI(next);
      return list.items.length > before;
    }

    return false;
  }

  let pumpFrame = 0;

  /**
   * Run the pump once layout has settled.
   *
   * Calling it straight after `render()` measures the sentinel against a
   * layout the browser hasn't finished reconciling: shrinking the grid from
   * 238 cards to a batch of 24 collapses the document height, and the scroll
   * position is clamped *after* that. Measured in the same tick, the sentinel
   * reads as far off-screen and the pump bails — leaving the grid stuck at
   * one batch. A frame later the geometry is real.
   */
  function schedulePump() {
    if (pumpFrame) return;
    pumpFrame = requestAnimationFrame(() => {
      pumpFrame = 0;
      void pump();
    });
  }

  async function pump() {
    if (advancing) return;
    advancing = true;
    try {
      // IntersectionObserver only reports *transitions*. When a render leaves
      // the sentinel still on screen — a filter narrow enough that one batch
      // doesn't fill the viewport — no further callback ever arrives and the
      // list stalls at 24 items. Keep stepping while the sentinel stays in
      // view instead of waiting to be told again.
      let guard = 0;
      while (sentinelInView() && (await step())) {
        if (++guard > 100) break; // never spin on a layout quirk
      }
    } finally {
      advancing = false;
    }
  }

  // ---- URL state ---------------------------------------------------------

  function writeUrl() {
    const params = list.toParams();
    const query = params.toString();
    const url = query ? `${location.pathname}?${query}` : location.pathname;
    history.replaceState(null, "", url);
  }

  // ---- wiring -------------------------------------------------------------

  const unsubs: Array<() => void> = [];

  const on = (
    target: EventTarget,
    type: string,
    handler: EventListenerOrEventListenerObject,
    opts?: AddEventListenerOptions,
  ) => {
    target.addEventListener(type, handler, opts);
    unsubs.push(() => target.removeEventListener(type, handler, opts));
  };

  // options
  for (const group of groups) {
    const field = group.dataset.field;
    if (!field) {
      console.warn("[int-filter] filter group missing data-field", group);
      continue;
    }

    on(group, "change", (e) => {
      const target = e.target as HTMLElement;
      if (!(target instanceof HTMLInputElement) || target.type !== "checkbox") {
        return;
      }
      const opt = target.closest<HTMLElement>('[int-filter="option"]');
      if (!opt) return;
      list.toggleFilter(field, optionValue(opt));
    });
  }

  // search
  if (searchInput) {
    let timer: ReturnType<typeof setTimeout>;

    on(searchInput, "input", () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        list.search(searchInput.value, ["name", "desc"]);
      }, SEARCH_DEBOUNCE);
    });

    // The input lives inside a Webflow form — Enter would submit it, and the
    // visitor gets "Oops! Something went wrong while submitting the form."
    //
    // Caught on keydown rather than on submit alone: implicit submission never
    // starts, so Webflow's own delegated submit handler never runs either and
    // the fix doesn't rest on which listener happened to bind first. The
    // submit guard stays as a backstop for a form with a real submit button.
    on(searchInput, "keydown", (e) => {
      if ((e as KeyboardEvent).key !== "Enter") return;
      e.preventDefault();
      clearTimeout(timer);
      list.search(searchInput.value, ["name", "desc"]);
    });

    // Warm page 2 the moment the user shows intent, so the first keystroke
    // doesn't wait on a network round trip.
    on(searchInput, "focus", () => loader.prefetch(), { once: true });

    unsubs.push(() => clearTimeout(timer));
  }

  // Both controls sit in Webflow forms, and *not the same one*: "Clear all"
  // is a `<button type="submit">` in a form of its own, so guarding only the
  // search input's form would leave that one live.
  const guarded = new Set<HTMLFormElement>();
  for (const el of [searchInput, resetBtn]) {
    const form = el?.closest("form");
    if (!form || guarded.has(form)) continue;
    guarded.add(form);
    on(form, "submit", (e) => e.preventDefault());
  }

  // any filter dropdown opening is also intent
  for (const group of groups) {
    on(group, "pointerenter", () => loader.prefetch(), { once: true });
  }

  // reset
  if (resetBtn) {
    on(resetBtn, "click", (e) => {
      e.preventDefault();
      if (searchInput) searchInput.value = "";
      list.reset();
    });
  }

  // chips
  if (chipsWrap) on(chipsWrap, "click", onChipClick);

  // ---- boot ----------------------------------------------------------------

  // Restore state from the URL before the first paint decision, so a shared
  // link never flashes the unfiltered grid.
  if (syncUrl && location.search) {
    const params = new URLSearchParams(location.search);
    // Drop Webflow's own pagination param — it isn't filter state.
    for (const key of [...params.keys()]) {
      if (key.endsWith("_page")) params.delete(key);
    }
    if ([...params.keys()].length) {
      list.fromParams(params, true);
      if (searchInput && list.searchQuery) searchInput.value = list.searchQuery;
    }
  }

  ready = true;

  {
    revealCount = isNarrowed() ? BATCH : Infinity;
    const result = list.getResult();
    // No entrance animation on boot — these cards are already painted, and
    // fading them in would look like a bug on a plain page load.
    render(result.active, false);
    updateUI(result);
    if (isNarrowed() && loader.hasMore) void loadRemaining();
  }

  observer.observe(sentinel);
  // Deep-linked filters can render a grid shorter than the viewport, which
  // would otherwise sit there with the sentinel visible and nothing loading.
  schedulePump();

  onDestroy(() => {
    observer.disconnect();
    if (pumpFrame) cancelAnimationFrame(pumpFrame);
    sentinel.remove();
    unsubs.forEach((off) => off());
    gsap.killTweensOf(list.items.map((i) => i.el));
  });
}
